import { ColumnDef } from '@tanstack/react-table';
import { useEffect, useState } from 'react';
import { Accordion, Button, Spinner, Stack } from 'react-bootstrap';
import { FaArrowLeft } from 'react-icons/fa6';
import { Link, To, useNavigate, useParams } from 'react-router-dom';
import { SFCAccordion } from '../../../../components/accordion/SFCAccordion';
import { DetailSection, LabelText } from '../../../../components/generic';
import CheckmarkCell from '../../../../components/tables/cellComponents/CheckmarkCell';
import CurrencyCell from '../../../../components/tables/cellComponents/CurrencyCell';
import { LinkCell } from '../../../../components/tables/cellComponents/LinkCell';
import { SimpleTable } from '../../../../components/tables/SimpleTable';
import { useToast } from '../../../../context/ToastContext';
import { useGetOrderQuery, useNotifyOrderMutation } from '../../../../services/endpoints/orders/orders';
import { Order } from '../../../../types/orders.type';
import { Registration } from '../../../../types/registration.type';
import { revisionsColumns } from '../../../../utils/consts/columnDefs/sessionColumns';
import { titleCase } from '../../../../utils/stringConversions';
import { getErrorMessage } from '../../../../utils/utils';

const LabelTextList = ({ label, list }: { label: string; list: string[] }) => (
  <Stack
    direction="vertical"
    gap={1}
  >
    <span
      className="fw-semibold"
      style={{ color: 'rgba(94, 100, 105, .5)' }}
    >
      {label}
    </span>
    {list.map((text, index) => (
      <span
        key={index}
        className="text-secondary fw-semibold"
      >
        {text}
      </span>
    ))}
  </Stack>
);

const getRegistrationColumns = (
  orderId?: number,
  orderRefunds: Order['refunds'] = [],
  isIncomplete = false
): ColumnDef<Registration>[] => [
  {
    header: 'ID',
    accessorKey: 'id',
    cell: ({ getValue }) => (
      <LinkCell
        content={getValue<number>().toString()}
        url={`/admin/registrations/${getValue<number>()}`}
      />
    )
  },
  {
    header: 'Session',
    id: 'session',
    cell: ({ row }) =>
      !row.original.course ? (
        ''
      ) : (
        <LinkCell
          content={row.original.course.title}
          url={`/admin/schedule/sessions/${row.original.course_id}`}
        />
      ),
    enableSorting: false
  },
  {
    header: 'Child',
    id: 'child',
    cell: ({ row }) => (!row.original.child ? '' : <p>{row.original.child.full_name}</p>),
    enableSorting: false
  },
  {
    header: 'Price',
    id: 'price',
    cell: ({ row }) => <CurrencyCell value={row.original.price.toFixed(2)} />,
    enableSorting: false
  },
  {
    header: 'Refunds',
    id: 'refund',
    enableSorting: false,
    cell: ({ row }) => {
      const refund = row.original.refund;
      const orderRefundsUrl = `/admin/orders/refunds?order_id=${orderId ?? row.original.order_id}`;

      if (refund) {
        return (
          <LinkCell
            content={`1 Refund: $${refund.total}`}
            url={`/admin/orders/refunds/${refund.id}`}
          />
        );
      }

      const refundTotal = orderRefunds.reduce((total, orderRefund) => total + Number(orderRefund.total ?? 0), 0);

      return (
        <LinkCell
          content={`${orderRefunds.length} Refunds: $${refundTotal}`}
          url={orderRefundsUrl}
        />
      );
    }
  },
  {
    header: 'Active',
    accessorKey: 'active_status',
    cell: ({ getValue }) => (
      <CheckmarkCell value={!isIncomplete && getValue<string>() === 'active'} />
    ),
    enableSorting: false
  }
];

export const OrderDetails = () => {
  const params = useParams();
  const navigate = useNavigate();
  const { addToast } = useToast();
  const { data, error, isLoading } = useGetOrderQuery(+params.id!);
  const [notifyOrder, { isLoading: isSendingConfirmation }] = useNotifyOrderMutation();
  const [order, setOrder] = useState<Order>();

  useEffect(() => {
    if (error && !isLoading) {
      addToast('Error while loading order', 'error');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, error, isLoading]);

  useEffect(() => {
    if (data) {
      setOrder(data.order);
    }
  }, [data]);

  const handleSendConfirmation = async () => {
    if (!order?.id) return;

    try {
      await notifyOrder(order.id).unwrap();
      addToast('Confirmation email sent', 'success');
    } catch (e) {
      addToast(getErrorMessage(e), 'error');
    }
  };

  if (isLoading) {
    return (
      <div className="w-100 vh-100 d-inline-flex">
        <Spinner
          variant="primary"
          className="m-auto"
        />
      </div>
    );
  }

  const detailsAccordion = (
    <SFCAccordion defaultActiveKey={['0', '1']}>
      <Accordion.Item eventKey="0">
        <Accordion.Header>Registrations</Accordion.Header>
        <Accordion.Body>
          <Stack gap={3}>
            <SimpleTable
              columns={getRegistrationColumns(
                order?.id,
                order?.refunds,
                !order?.transaction_id
              )}
              data={order?.registrations ?? []}
            />
            <Link
              to={`/admin/registrations?order_id=${order?.id}`}
              className="text-info"
            >
              Manage/Refund Registrations
            </Link>
          </Stack>
        </Accordion.Body>
      </Accordion.Item>
      <Accordion.Item eventKey="1">
        <Accordion.Header>Revisions</Accordion.Header>
        <Accordion.Body>
          <div className="d-none d-lg-block">
            <SimpleTable
              columns={revisionsColumns.filter((c) => c.header !== 'Comment')}
              data={order?.audits ?? []}
            />
          </div>
        </Accordion.Body>
      </Accordion.Item>
    </SFCAccordion>
  );

  return (
    <>
      <div
        className="d-flex justify-content-between align-items-center px-lg-5 border-bottom px-4 py-3"
        style={{ background: '#EFEFEF' }}
      >
        <Stack
          direction="horizontal"
          gap={2}
          className="ps-lg-3 ps-0"
        >
          <button
            className="btn btn-link text-info p-0"
            onClick={() => navigate(-1 as To)}
          >
            <FaArrowLeft
              size={20}
              className="d-lg-none"
            />
            <FaArrowLeft
              size={24}
              className="d-none d-lg-block"
            />
          </button>
          <h2
            className="d-lg-none text-secondary fw-semibold text-uppercase mb-0 pt-1"
            style={{ color: '#888888', fontSize: '1rem' }}
          >
            Orders List
          </h2>
          <h2 className="d-none d-lg-block text-info fw-bold mb-0">Order #{order?.id}</h2>
        </Stack>
      </div>
      <div className="d-lg-none">
        <Stack
          gap={4}
          className="p-4"
        >
          <DetailSection>
            <LinkCell
              content={`${order?.parent.first_name} ${order?.parent.last_name}`}
              url={`/admin/sfc-users/parents/${order?.parent_id}`}
            />
            <LabelText
              label="Theme:"
              text="Theme"
            />
          </DetailSection>

          {detailsAccordion}
        </Stack>
      </div>
      <div className="d-none d-lg-flex">
        <div
          className="w-100 mt-5 px-5 py-4"
          style={{ maxWidth: 'calc(100% - 400px)' }}
        >
          {detailsAccordion}
        </div>
        <Stack
          gap={4}
          className="vh-100 p-4"
          style={{ width: 400, background: '#F2F2F2' }}
        >
          <DetailSection title="Details">
            <Stack>
              <LinkCell
                content={order?.parent.full_name}
                url={`/admin/sfc-users/parents/${order?.parent_id}`}
              />
              <span>{`${order?.parent.address}`}</span>
              <span>{`${order?.parent.city}, ${order?.parent.state}, ${order?.parent.zip}`}</span>
            </Stack>
            <LabelText
              label="Email:"
              text={order?.parent.email}
            />

            <LabelText
              label="How did you hear about us?"
              text={order?.parent.heard_about || '—'}
            />

            <LabelText label="Session(s):" />
            <Stack>
              {order?.registrations.map((r) => (
                <LinkCell
                  key={r.id}
                  content={r.course?.title}
                  url={`/admin/schedule/sessions/${r.course_id}`}
                />
              ))}
            </Stack>

            <LabelText label="Total:">
              <CurrencyCell value={order?.total ?? 0} />
            </LabelText>

            {/* TODO: Add debits and fix discount summaries */}
            {/* {order?.debits ? (
              <LabelText label="Credits Used:" >
                <CurrencyCell value={order?.debits?.reduce((acc, d) => (acc + d.amount))} />
              </LabelText>
            ) : (<></>)} */}

            {order?.discount ? (
              <LabelText label="Scholarship:">
                <span className="text-capitalize">
                  {titleCase(order?.discount?.scholarship?.scholarship_type)} (
                  {order?.discount?.scholarship?.percent_discount}%&nbsp;off)
                </span>
              </LabelText>
            ) : (
              <></>
            )}

            {order?.transaction_id ? (
              <Stack>
                <LabelText label="Status:">
                  <span style={{ color: 'green' }}>Complete</span>
                </LabelText>
                <Button
                  variant="primary"
                  size="sm"
                  disabled={isSendingConfirmation}
                  onClick={handleSendConfirmation}
                >
                  {isSendingConfirmation ? 'Sending...' : 'Send Confirmation Email'}
                </Button>
              </Stack>
            ) : (
              <LabelText label="Status:">
                <span style={{ color: 'red' }}>Incomplete</span>
              </LabelText>
            )}
          </DetailSection>
        </Stack>
      </div>
    </>
  );
};
